import { useCallback, useState, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import * as Popover from "@radix-ui/react-popover";
import { AnimatePresence, motion } from "framer-motion";
import { FileText, Tags, ChevronDown, Plus, X } from "lucide-react";
import { springs } from "@/components/ui/motion";
import { Input, Textarea } from "@/components/ui/primitives";
import { Dropdown } from "@/components/ui/Dropdown";
import { useCreateAuditTag } from "@/hooks/use-audit-tags";
import { TAG_COLORS } from "@/components/ui/Tag/types";
import { DatePicker } from "@/components/ui/DatePicker";
import { TRIGGER_LABEL_KEYS, TRIGGER_OPTIONS } from "./wizardData";
import { ChecklistSourceSelector } from "./ChecklistSourceSelector";
import { AuditTypeSelector } from "./AuditTypeSelector";
import type { WorkspaceStandardOption } from "./AuditTypeSelector";
import type { NewAuditForm, ChecklistSource } from "./types";
import type { AuditTemplate } from "@/hooks/use-audit-templates";
import type { AuditTag } from "@/hooks/use-audit-tags";
import type { WorkspaceAuditType } from "@/hooks/use-workspace-audit-types";
import { useCreateWorkspaceAuditType } from "@/hooks/use-workspace-audit-types";
import type { Id } from "@/packages/types";
import type { WorkspaceAuditTypeId } from "@/packages/types";
import { useTranslation } from "@/packages/utils";

interface Step1DetailsFormProps {
  form: NewAuditForm;
  onChange: (form: NewAuditForm) => void;
  onChecklistSourceChange: (source: ChecklistSource) => void;
  templates: AuditTemplate[];
  tags: AuditTag[];
  workspaceTypes: WorkspaceAuditType[];
  workspaceStandards: WorkspaceStandardOption[];
  /** Set of field keys that failed validation */
  fieldErrors?: Set<string>;
  /** Incremented on each validation attempt to re-trigger shake */
  validationAttempt?: number;
  /** Selected workspace standard ID (when a standard is chosen from AuditTypeSelector) */
  sourceStandardId: string | null;
  onSourceStandardIdChange: (id: string | null) => void;
}

function SectionHeader({
  icon,
  children,
}: {
  icon: ReactNode;
  children: string;
}) {
  return (
    <div className="mb-3 flex items-center gap-2.5">
      <div className="flex h-6 w-6 items-center justify-center rounded-md bg-accent/10 text-accent">
        {icon}
      </div>
      <h3 className="text-[11px] font-medium uppercase tracking-wider text-secondary">
        {children}
      </h3>
    </div>
  );
}

function FieldLabel({
  children,
  hasError,
}: {
  children: string;
  hasError?: boolean;
}) {
  return (
    <span
      className={`mb-1.5 block text-[12px] font-medium ${hasError ? "text-red-500" : "text-secondary"}`}
    >
      {children}
    </span>
  );
}

function StaggerItem({
  index,
  children,
  hasError,
  shakeKey,
}: {
  index: number;
  children: ReactNode;
  hasError?: boolean;
  shakeKey?: number;
}) {
  const [shouldShake, setShouldShake] = useState(false);

  useEffect(() => {
    if (shakeKey && hasError) {
      setShouldShake(true);
      const timer = setTimeout(() => setShouldShake(false), 500);
      return () => clearTimeout(timer);
    }
  }, [shakeKey, hasError]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={
        shouldShake
          ? { opacity: 1, y: 0, x: [0, -4, 4, -3, 3, 0] }
          : { opacity: 1, y: 0, x: 0 }
      }
      transition={
        shouldShake
          ? {
              ...springs.gentle,
              delay: index * 0.04,
              x: { duration: 0.4, ease: "easeInOut" },
            }
          : { ...springs.gentle, delay: index * 0.04 }
      }
    >
      {children}
    </motion.div>
  );
}

export function Step1DetailsForm({
  form,
  onChange,
  onChecklistSourceChange,
  templates,
  tags,
  workspaceTypes,
  workspaceStandards,
  fieldErrors,
  validationAttempt,
  sourceStandardId,
  onSourceStandardIdChange,
}: Step1DetailsFormProps) {
  const { t } = useTranslation(["audits"]);
  const { create: createWorkspaceAuditType } = useCreateWorkspaceAuditType();

  const update = useCallback(
    (field: keyof NewAuditForm, value: string | string[]) => {
      onChange({ ...form, [field]: value });
    },
    [form, onChange],
  );

  const selectedTemplate = templates.find(
    (tmpl) => tmpl.id === form.templateId,
  );
  const selectedTemplateId =
    (selectedTemplate?.id as Id<"auditTemplates">) ?? null;

  const isAuditTypeSelected =
    form.templateId !== "" ||
    form.workspaceAuditTypeId !== "" ||
    sourceStandardId !== null;

  const handleAuditTypeChange = useCallback(
    (selection: {
      templateId: string;
      workspaceAuditTypeId: string;
      sourceStandardId?: string;
    }) => {
      const currentYear = new Date().getFullYear();

      // Determine the new display name based on selection type
      let autoName = "";
      if (selection.sourceStandardId) {
        const standard = workspaceStandards.find(
          (s) => s._id === selection.sourceStandardId,
        );
        autoName = standard ? `${standard.auditTypeName} ${currentYear}` : "";
      } else if (selection.templateId) {
        const tmpl = templates.find((t) => t.id === selection.templateId);
        autoName = tmpl ? `${tmpl.name} ${currentYear}` : "";
      } else if (selection.workspaceAuditTypeId) {
        const wsType = workspaceTypes.find(
          (t) => t.id === selection.workspaceAuditTypeId,
        );
        autoName = wsType ? `${wsType.name} ${currentYear}` : "";
      }

      // Determine previous auto name
      const prevTemplate = selectedTemplate;
      const prevWsType = workspaceTypes.find(
        (t) => t.id === form.workspaceAuditTypeId,
      );
      const prevStandard = sourceStandardId
        ? workspaceStandards.find((s) => s._id === sourceStandardId)
        : null;
      const prevType = prevStandard
        ? { name: prevStandard.auditTypeName }
        : (prevTemplate ?? prevWsType);
      const prevAutoName = prevType ? `${prevType.name} ${currentYear}` : "";
      const shouldAutoFill =
        form.displayName === "" || form.displayName === prevAutoName;

      // Update sourceStandardId
      onSourceStandardIdChange(selection.sourceStandardId ?? null);

      // When a workspace standard is selected, auto-set checklistSource to "upload"
      const newChecklistSource = selection.sourceStandardId ? "upload" : null;

      onChange({
        ...form,
        templateId: selection.templateId,
        workspaceAuditTypeId: selection.workspaceAuditTypeId,
        displayName: shouldAutoFill ? autoName : form.displayName,
        checklistSource: newChecklistSource,
      });
    },
    [
      form,
      onChange,
      templates,
      workspaceTypes,
      workspaceStandards,
      selectedTemplate,
      sourceStandardId,
      onSourceStandardIdChange,
    ],
  );

  const handleCreateWorkspaceType = useCallback(
    async (args: { name: string; code: string; description?: string }) => {
      const newId = await createWorkspaceAuditType(args);
      const currentYear = new Date().getFullYear();
      const autoName = `${args.name} ${currentYear}`;
      const shouldAutoFill =
        form.displayName === "" ||
        form.displayName ===
          (selectedTemplate
            ? `${selectedTemplate.name} ${currentYear}`
            : workspaceTypes.find((t) => t.id === form.workspaceAuditTypeId)
              ? `${workspaceTypes.find((t) => t.id === form.workspaceAuditTypeId)?.name} ${currentYear}`
              : "");

      onSourceStandardIdChange(null);
      onChange({
        ...form,
        templateId: "",
        workspaceAuditTypeId: newId as WorkspaceAuditTypeId,
        displayName: shouldAutoFill ? autoName : form.displayName,
        checklistSource: null,
      });
    },
    [
      createWorkspaceAuditType,
      form,
      onChange,
      onSourceStandardIdChange,
      selectedTemplate,
      workspaceTypes,
    ],
  );

  const handleDeadlineChange = useCallback(
    (date: Date | undefined) => {
      update("deadline", date ? date.toISOString() : "");
    },
    [update],
  );

  const toggleTag = useCallback(
    (tagId: string) => {
      const next = form.tagIds.includes(tagId)
        ? form.tagIds.filter((t) => t !== tagId)
        : [...form.tagIds, tagId];
      update("tagIds", next);
    },
    [form.tagIds, update],
  );

  const deadlineDate = form.deadline ? new Date(form.deadline) : undefined;

  // Hide ChecklistSourceSelector when a workspace standard is selected
  const showChecklistSourceSelector = sourceStandardId === null;

  return (
    <div className="space-y-4 pt-2">
      {/* Core Details */}
      <div className="rounded-card border border-border/60 bg-card/30 p-4">
        <SectionHeader icon={<FileText className="h-3.5 w-3.5" />}>
          {t("audits:wizard.coreDetails")}
        </SectionHeader>
        <div className="space-y-3">
          <StaggerItem
            index={0}
            hasError={fieldErrors?.has("auditType")}
            shakeKey={validationAttempt}
          >
            <FieldLabel hasError={fieldErrors?.has("auditType")}>
              {t("audits:wizard.auditType")}
            </FieldLabel>
            <AuditTypeSelector
              templates={templates}
              workspaceTypes={workspaceTypes}
              workspaceStandards={workspaceStandards}
              selectedTemplateId={form.templateId}
              selectedWorkspaceTypeId={form.workspaceAuditTypeId}
              sourceStandardId={sourceStandardId}
              onChange={handleAuditTypeChange}
              onCreateWorkspaceType={handleCreateWorkspaceType}
            />
          </StaggerItem>

          {showChecklistSourceSelector && (
            <StaggerItem
              index={1}
              hasError={fieldErrors?.has("checklistSource")}
              shakeKey={validationAttempt}
            >
              <FieldLabel hasError={fieldErrors?.has("checklistSource")}>
                {t("audits:wizard.checklistSource")}
              </FieldLabel>
              <ChecklistSourceSelector
                selectedTemplateId={selectedTemplateId}
                isAuditTypeSelected={isAuditTypeSelected}
                value={form.checklistSource}
                onChange={onChecklistSourceChange}
              />
            </StaggerItem>
          )}

          <StaggerItem
            index={2}
            hasError={fieldErrors?.has("displayName")}
            shakeKey={validationAttempt}
          >
            <FieldLabel hasError={fieldErrors?.has("displayName")}>
              {t("audits:wizard.auditName")}
            </FieldLabel>
            <Input
              icon={FileText}
              placeholder={t("audits:wizard.auditNamePlaceholder")}
              value={form.displayName}
              onChange={(e) => update("displayName", e.target.value)}
              className={
                fieldErrors?.has("displayName") ? "border-red-500/50" : ""
              }
            />
          </StaggerItem>

          <StaggerItem
            index={3}
            hasError={fieldErrors?.has("trigger")}
            shakeKey={validationAttempt}
          >
            <FieldLabel hasError={fieldErrors?.has("trigger")}>
              {t("audits:wizard.trigger")}
            </FieldLabel>
            <Dropdown>
              <Dropdown.Trigger>
                <button
                  type="button"
                  className={`flex w-full items-center justify-between rounded-button border bg-card px-3 py-2 text-[14px] text-left cursor-pointer ${
                    fieldErrors?.has("trigger")
                      ? "border-red-500/50"
                      : "border-border"
                  }`}
                >
                  <span
                    className={form.trigger ? "text-primary" : "text-muted"}
                  >
                    {form.trigger && form.trigger in TRIGGER_LABEL_KEYS
                      ? t(
                          TRIGGER_LABEL_KEYS[
                            form.trigger as (typeof TRIGGER_OPTIONS)[number]
                          ],
                        )
                      : form.trigger || t("audits:wizard.selectTrigger")}
                  </span>
                  <ChevronDown className="h-4 w-4 text-muted" />
                </button>
              </Dropdown.Trigger>
              <Dropdown.Content side="bottom" align="start">
                <Dropdown.Label>{t("audits:wizard.trigger")}</Dropdown.Label>
                {TRIGGER_OPTIONS.map((trigger) => (
                  <Dropdown.Item
                    key={trigger}
                    onSelect={() => update("trigger", trigger)}
                  >
                    {t(TRIGGER_LABEL_KEYS[trigger])}
                  </Dropdown.Item>
                ))}
              </Dropdown.Content>
            </Dropdown>
          </StaggerItem>

          <StaggerItem
            index={4}
            hasError={fieldErrors?.has("deadline")}
            shakeKey={validationAttempt}
          >
            <FieldLabel hasError={fieldErrors?.has("deadline")}>
              {t("audits:wizard.deadline")}
            </FieldLabel>
            <DatePicker
              value={deadlineDate}
              onChange={handleDeadlineChange}
              minDate={new Date()}
            >
              <DatePicker.Trigger
                variant="input"
                className={`w-full ${fieldErrors?.has("deadline") ? "border-red-500/50" : ""}`}
              />
              <Popover.Portal>
                <Popover.Content
                  side="bottom"
                  align="start"
                  sideOffset={4}
                  className="z-50 rounded-[12px] border border-border bg-card shadow-lg"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                >
                  <div className="flex">
                    <DatePicker.Presets />
                    <DatePicker.Calendar />
                  </div>
                  <DatePicker.Actions />
                </Popover.Content>
              </Popover.Portal>
            </DatePicker>
          </StaggerItem>
        </div>
      </div>

      {/* Classification */}
      <div className="rounded-card border border-border/60 bg-card/30 p-4">
        <SectionHeader icon={<Tags className="h-3.5 w-3.5" />}>
          {t("audits:wizard.classification")}
        </SectionHeader>
        <div className="space-y-3">
          <StaggerItem index={6}>
            <FieldLabel>{t("audits:wizard.tags")}</FieldLabel>
            <InlineTagSelector
              tags={tags}
              selectedTagIds={form.tagIds}
              onToggle={toggleTag}
            />
          </StaggerItem>

          <StaggerItem index={7}>
            <FieldLabel>{t("audits:wizard.internalNotes")}</FieldLabel>
            <Textarea
              placeholder={t("audits:wizard.internalNotesPlaceholder")}
              rows={2}
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
            />
          </StaggerItem>
        </div>
      </div>
    </div>
  );
}

/* ── Inline tag selector with create ── */

function InlineTagSelector({
  tags,
  selectedTagIds,
  onToggle,
}: {
  tags: AuditTag[];
  selectedTagIds: string[];
  onToggle: (tagId: string) => void;
}) {
  const { t } = useTranslation(["audits"]);
  const createAuditTag = useCreateAuditTag();
  const [showCreate, setShowCreate] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showCreate) {
      const timer = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [showCreate]);

  const handleCreate = async () => {
    const name = newTagName.trim();
    if (!name || isCreating) return;
    setIsCreating(true);
    try {
      const color = TAG_COLORS[tags.length % TAG_COLORS.length];
      const newId = await createAuditTag({ name, color });
      onToggle(String(newId));
      setNewTagName("");
      setShowCreate(false);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {tags.map((tag) => {
        const selected = selectedTagIds.includes(tag.id);
        return (
          <motion.button
            key={tag.id}
            type="button"
            onClick={() => onToggle(tag.id)}
            whileTap={{ scale: 0.92 }}
            transition={springs.snappy}
            className={`rounded-full px-3 py-1 text-[12px] font-medium transition-colors cursor-pointer ${
              selected
                ? "shadow-sm"
                : "bg-card text-secondary hover:text-primary"
            }`}
            style={
              selected
                ? {
                    backgroundColor: `${tag.color}26`,
                    color: tag.color,
                    boxShadow: `0 1px 2px ${tag.color}1A`,
                  }
                : undefined
            }
          >
            <span
              className="mr-1.5 inline-block h-2 w-2 rounded-full"
              style={{ backgroundColor: tag.color }}
            />
            {tag.name}
          </motion.button>
        );
      })}

      <AnimatePresence mode="wait">
        {showCreate ? (
          <motion.div
            key="create-form"
            initial={{ opacity: 0, width: 0 }}
            animate={{ opacity: 1, width: "auto" }}
            exit={{ opacity: 0, width: 0 }}
            transition={{ duration: 0.15 }}
            className="flex items-center gap-1.5 overflow-hidden"
          >
            <input
              ref={inputRef}
              type="text"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreate();
                if (e.key === "Escape") {
                  setShowCreate(false);
                  setNewTagName("");
                }
              }}
              placeholder={t("audits:wizard.newTagPlaceholder")}
              className="h-[26px] w-28 rounded-full border border-accent/40 bg-card px-2.5 text-[12px] text-primary outline-none focus:border-accent"
            />
            <button
              type="button"
              onClick={() => {
                setShowCreate(false);
                setNewTagName("");
              }}
              className="p-0.5 text-muted hover:text-primary transition-colors"
            >
              <X size={12} />
            </button>
          </motion.div>
        ) : (
          <motion.button
            key="add-button"
            type="button"
            onClick={() => setShowCreate(true)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="rounded-full border border-dashed border-border px-3 py-1 text-[12px] font-medium text-muted hover:text-secondary hover:border-muted transition-colors cursor-pointer flex items-center gap-1"
          >
            <Plus size={12} />
            {t("audits:wizard.addTag")}
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
